// Copyright (c) 2025 by Audere

import {
  DirectionOfTranslation,
  FormLabelData,
  LabelData,
  MessageData,
} from "../features/api/labelApiSlice";
import {
  LabelKeys,
  LabelTask,
  LabeledContent,
  Question,
  getQuestions,
} from "../features/label/labelTypes";
import { getTaskLabels } from "./labelUtils";
import {
  QuestionResponse,
  isValidValue,
  validateQuestion,
} from "./validateQuestion";
import { debounce, omit } from "lodash";

export enum CompletionStatus {
  new = "new",
  incomplete = "incomplete",
  complete = "complete",
}

type MessageUID = string;
export type MessageStatus = Record<MessageUID, CompletionStatus>;
export type TaskStatusesMap<Keys extends readonly string[]> = {
  [K in Keys[number]]: K extends `${string}message${string}`
    ? MessageStatus
    : CompletionStatus;
};
export type LabelTaskKey = LabelKeys[number];
export type LastUpdatedTasksMap = Partial<Record<LabelTaskKey, string>>;

export type QCMessageCorrection = Record<MessageUID, boolean | null>;
export type QCCorrectLabelsMap<Keys extends readonly string[]> = {
  [K in Keys[number]]: K extends `${string}message${string}`
    ? QCMessageCorrection
    : boolean | null;
};

export const isMessageTask = (taskContent: LabeledContent) => {
  return taskContent === LabeledContent.message;
};

export const isStructuredDataTask = (taskContent: LabeledContent) => {
  return taskContent === LabeledContent.structuredData;
};

export const isSummaryTask = (taskContent: LabeledContent) => {
  return taskContent === LabeledContent.summary;
};

export const isTranslatedMessageTask = (taskContent: LabeledContent) => {
  return taskContent === LabeledContent.translatedMessage;
};

const evaluateAnswers = (
  fieldAnswers: FormLabelData,
  questions: Question[],
  statusFlags: { anyTouched: boolean; allValid: boolean }
) => {
  Object.entries(fieldAnswers).forEach(([key, value]) => {
    const question = questions.find((q) => q.id === key);
    // perFlag questions (e.g. appropriateFlag) are answered once per flag on the
    // conversation, so every flag's answer must be valid for the question to be complete.
    if (question?.perFlag) {
      const flagAnswers = Object.values(value as Record<string, QuestionResponse>);
      statusFlags.anyTouched ||= flagAnswers.some((flagAnswer) =>
        Object.values(flagAnswer).some(isValidValue)
      );
      statusFlags.allValid &&= flagAnswers.every((flagAnswer) =>
        validateQuestion(question, flagAnswer)
      );
      return;
    }
    const answer = value as QuestionResponse;
    // Keep true if any question is touched
    statusFlags.anyTouched ||= Object.values(answer).some(isValidValue);
    // Keep false if any question is invalid
    if (question) {
      statusFlags.allValid &&= validateQuestion(question, answer);
    }
  });
};

export const getFormStatus = (
  labelData: FormLabelData,
  task: LabelTask,
  labelingQuestions: Question[],
  isQC: boolean = false
): CompletionStatus => {
  if ("correct_label" in labelData) {
    if (labelData.correct_label) {
      return CompletionStatus.complete;
    } else if (labelData.correct_label === null) {
      return CompletionStatus.new;
    }
  }
  const questions = getQuestions(task.questionIDs, labelingQuestions);
  const statusFlags = { anyTouched: false, allValid: true };

  // Treat each structured data field as a sub-form
  const onlyAnswers = omit(labelData, "correct_label");
  const formAnswers: FormLabelData[] = isStructuredDataTask(task.content)
    ? (Object.values(onlyAnswers) as FormLabelData[])
    : [onlyAnswers];

  formAnswers.forEach((answers) => {
    evaluateAnswers(answers, questions, statusFlags);
  });

  if (
    !statusFlags.anyTouched &&
    (!isQC || Object.keys(onlyAnswers).length === 0)
  )
    return CompletionStatus.new;
  return statusFlags.allValid
    ? CompletionStatus.complete
    : CompletionStatus.incomplete;
};

const setTaskStatus = (
  task: LabelTask,
  existingLabels: LabelData[],
  taskStatuses: Record<string, MessageStatus | CompletionStatus>,
  labelingQuestions: Question[],
  message?: MessageData
): void => {
  if (
    (isMessageTask(task.content) || isTranslatedMessageTask(task.content)) &&
    message
  ) {
    const questions = getQuestions(
      task.questionIDs,
      labelingQuestions,
      message.role
    );
    if (questions.length === 0) {
      return;
    }
  }

  const labels = getTaskLabels(task.id, existingLabels, message?.message_uid);
  const formStatus = labels
    ? getFormStatus(
        "qc_data" in labels ? (labels.qc_data ?? {}) : labels.label_data,
        task,
        labelingQuestions,
        "qc_data" in labels
      )
    : CompletionStatus.new;
  if (
    (isMessageTask(task.content) || isTranslatedMessageTask(task.content)) &&
    message?.message_uid
  ) {
    const msgStatus: MessageStatus = {
      ...((typeof taskStatuses[task.id] === "object"
        ? taskStatuses[task.id]
        : {}) as MessageStatus),
      [message.message_uid]: formStatus,
    };
    taskStatuses[task.id] = msgStatus;
  } else {
    taskStatuses[task.id] = formStatus;
  }
};

export const getInitialTaskStatuses = <const Keys extends readonly string[]>(
  tasks: LabelTask[],
  existingLabels: LabelData[],
  conversationMsgs: MessageData[],
  labelingQuestions: Question[]
): TaskStatusesMap<Keys> => {
  const statuses = {} as Record<string, MessageStatus | CompletionStatus>;

  tasks.forEach((task) => {
    if (isMessageTask(task.content) || isTranslatedMessageTask(task.content)) {
      conversationMsgs.forEach((msg) => {
        setTaskStatus(task, existingLabels, statuses, labelingQuestions, msg);
      });
    } else {
      setTaskStatus(task, existingLabels, statuses, labelingQuestions);
    }
  });
  return statuses as TaskStatusesMap<Keys>;
};

export const getTaskCompletionStatus = (
  taskStatuses: TaskStatusesMap<LabelKeys> | undefined,
  taskID: string,
  messageUID?: string
): CompletionStatus | undefined => {
  const task = taskStatuses?.[taskID as keyof typeof taskStatuses];
  if (typeof task === "object") {
    if (messageUID) return task[messageUID];
    const allComplete = Object.values(task).every(
      (status) => status === CompletionStatus.complete
    );
    const allNew = Object.values(task).every(
      (status) => status === CompletionStatus.new
    );
    if (allComplete) {
      return CompletionStatus.complete;
    } else if (allNew) {
      return CompletionStatus.new;
    } else {
      return CompletionStatus.incomplete;
    }
  } else if (typeof task === "string") {
    return task;
  }
};

export const getQCCorrection = (
  qcCorrections: QCCorrectLabelsMap<LabelKeys> | undefined,
  taskID: string,
  messageUID?: string
) => {
  const correction = qcCorrections?.[taskID as keyof typeof qcCorrections];
  if (correction && typeof correction === "object") {
    return messageUID ? correction[messageUID] : null;
  }
  return correction;
};

const getQCCorrectLabel = (
  taskID: string,
  existingLabels: LabelData[],
  correct_labels: Record<string, QCMessageCorrection | boolean | null>,
  message_uid?: string
) => {
  const label = getTaskLabels(taskID, existingLabels, message_uid);
  if (label?.qc_data && "correct_label" in label.qc_data) {
    if (message_uid) {
      correct_labels[taskID] = {
        ...(typeof correct_labels[taskID] === "object"
          ? correct_labels[taskID]
          : {}),
        [message_uid]: label.qc_data.correct_label as boolean,
      };
    } else {
      correct_labels[taskID] = label.qc_data.correct_label as boolean;
    }
  } else {
    correct_labels[taskID] = message_uid
      ? {
          ...(typeof correct_labels[taskID] === "object"
            ? correct_labels[taskID]
            : {}),
          [message_uid]: null,
        }
      : null;
  }
};

export const getInitialQCCorrectLabels = <const Keys extends readonly string[]>(
  tasks: LabelTask[],
  existingLabels: LabelData[],
  conversationMsgs: MessageData[],
  labelingQuestions: Question[]
): QCCorrectLabelsMap<Keys> => {
  const correct_labels = {} as Record<
    string,
    QCMessageCorrection | boolean | null
  >;

  tasks.forEach((task) => {
    if (isMessageTask(task.content) || isTranslatedMessageTask(task.content)) {
      conversationMsgs.forEach((msg) => {
        const questions = getQuestions(
          task.questionIDs,
          labelingQuestions,
          msg.role
        );
        if (questions.length === 0) return; // Skip if no questions for the message

        getQCCorrectLabel(
          task.id,
          existingLabels,
          correct_labels,
          msg.message_uid
        );
      });
    } else {
      getQCCorrectLabel(task.id, existingLabels, correct_labels);
    }
  });
  return correct_labels as QCCorrectLabelsMap<Keys>;
};

export const generateFormDefaultValues = (
  data: FormLabelData
): FormLabelData => {
  return Object.entries(data).reduce<FormLabelData>((acc, [key, value]) => {
    if (typeof value === "object" && value !== null && "response" in value) {
      if (typeof (value as { response: unknown }).response === "string") {
        acc[key] = { response: "" };
      } else if (Array.isArray((value as { response: unknown }).response)) {
        acc[key] = { response: [] };
      }
    } else if (
      typeof value === "object" &&
      value !== null &&
      "structuredDataCorrectness" in value
    ) {
      acc[key] = { structuredDataCorrectness: { response: "" } };
    } else if (
      typeof value === "object" &&
      value !== null &&
      Object.values(value).every(
        (subValue) =>
          typeof subValue === "object" && subValue !== null && "response" in subValue
      )
    ) {
      // perFlag questions (e.g. appropriateFlag) are stored as a map of flag id ->
      // { response }; reset each flag's response independently.
      acc[key] = Object.keys(value).reduce<FormLabelData>((subAcc, subKey) => {
        subAcc[subKey] = { response: "" };
        return subAcc;
      }, {});
    }
    return acc;
  }, {});
};

export const debounceForDataChange = debounce((fn: () => void) => {
  fn();
}, 5000);

export const getQCCorrectedLabels = <T extends FormLabelData>(
  source: T,
  target: T
): Partial<T> | T =>
  (Object.keys(target) as (keyof T)[]).reduce<Partial<T>>((diff, key) => {
    const sourceValue = source[key];
    const targetValue = target[key];
    if (
      !(key in source) ||
      JSON.stringify(sourceValue) !== JSON.stringify(targetValue)
    ) {
      diff[key] = targetValue;
    }
    return diff;
  }, {});

export const getMessageText = (
  message: MessageData,
  showTranslated = false
): string => {
  const translation = message.translation;
  if (!translation) return message.text;

  const isInternalToNative =
    translation.direction_of_translation ===
    DirectionOfTranslation.internalToNative;
  
  // If showTranslated is true, returns the translated text; otherwise, returns the original text
  return showTranslated
    ? isInternalToNative
      ? translation.native_text
      : translation.internal_text
    : isInternalToNative
    ? translation.internal_text
    : translation.native_text;
};
